import { CommonModule, Location } from '@angular/common';
import { Component, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject, Observable, catchError, map, merge, mergeMap, of, shareReplay, take, tap } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';

import { NgxSpinnerModule } from 'ngx-spinner';

import { OfferService } from '../../../_services/offer/offer.service';
import { AuthService } from '../../../_services/auth.service';
import { SharedService } from '../../../_services/shared.service';
import { ShoppingCartService } from '../../../_services/shopping-cart.service';
import { BusyService } from '../../../_services/busy.service';

import { Offer } from '../../../model/offer';
import { UpgradeAvailabilityDTO } from '../../../model/upgradeAvailabilityDTO';
import { OfferExpiresDTO } from '../../../model/offerExpiresDTO';

import { UiNewOfferCardListResponsiveComponent } from '../ui-new-offer-card-list-responsive/ui-new-offer-card-list-responsive.component';

import { UiNewShellComponent, UiNewStyle, UiNewTheme } from '../ui-new-shell/ui-new-shell.component';
import { UiNewTopBarComponent } from '../ui-new-top-bar/ui-new-top-bar.component';
import { UI_NEW_VIEW_MODE_STORAGE_KEY, UiNewTabsBarComponent, UiNewViewMode } from '../ui-new-tabs-bar/ui-new-tabs-bar.component';
import { UiNewOfferTab } from '../ui-new-offer-nav-bar/ui-new-offer-nav-bar.component';
import { UiNewFilterPanelComponent } from '../ui-new-filter-panel/ui-new-filter-panel.component';
import { UiNewViewBarComponent } from '../ui-new-view-bar/ui-new-view-bar.component';
import { AttentionFlashService, AttentionFlashTab } from '../../../_services/attention-flash.service';
import { LxResponsiveClassDirective } from '../../../directives/lx-responsive-class.directive';
import { ThemeService } from '../../../_services/theme.service';
import { OfferCategoryService } from '../../../_services/offer-category.service';
import { ExistingOffersetDTO } from '@app/model/exisitingOffersetDTO';

@Component({
  selector: 'ui-new-home',
  standalone: true,
  imports: [
    CommonModule,
    NgxSpinnerModule,
    LxResponsiveClassDirective,
    UiNewShellComponent,
    UiNewTopBarComponent,
    UiNewTabsBarComponent,
    UiNewFilterPanelComponent,
    UiNewViewBarComponent,
  ],
  providers: [AuthService, OfferService],
  templateUrl: './ui-new-home.component.html',
  styleUrl: './ui-new-home.component.scss',
})
export class UiNewHomeComponent implements OnInit, OnDestroy {
  @ViewChild(UiNewFilterPanelComponent) filterPanelComponent?: UiNewFilterPanelComponent;

  uiNewOfferListComponent = UiNewOfferCardListResponsiveComponent;

  readonly pageResponsiveClass = {
    horizontal: 'page-wrap-desktop',
    vertical: 'page-wrap-handset',
  } as const;

  private static readonly EMPTY_OFFER_SET_ID = '00000000-0000-0000-0000-000000000000';

  theme: UiNewTheme = 'dark';
  style: UiNewStyle = 'neo';
  logoUrl: string | null = null;

  offers: Offer[] = [];
  fullOffers: Offer[] = [];
  offers$: Observable<Offer[]> = of([]);
  filteredOffers$: Observable<Offer[]> = of([]);
  offerCount: number | null = null;

  offerAliasId = '';
  propertyId = '';

  loadingPage = true;
  initialPageLoad = true;
  themeReady = false;
  showLandingMessage = false;

  requestOnlyMode = false;

  upgradeAvailability: UpgradeAvailabilityDTO = {
    room_available: false,
    eci_available: false,
    lco_available: false,
    addon_available: false,
  };

  selectedTab: UiNewOfferTab = 'ROOM';
  viewMode: UiNewViewMode = 'tiles';
  filtersOpen = false;

  viewMoreToggle = false;
  viewMoreOrLessStr: string = 'View More';

  endDate: Date = new Date();

  tags: string[] = [];
  bedTypes: string[] = [];
  tagsDict: { [key: string]: { flg_enabled: boolean } } = {};
  bedTypesDict: { [key: string]: { flg_enabled: boolean } } = {};
  tagsOfOfferIds: { [key: string]: string[] } = {};
  bedTypesOfOfferIds: { [key: string]: string[] } = {};

  hasFeaturedOffers = false;
  featuredOnly = false;

  hasFavoriteOffers = false;
  favoriteOnly = false;

  private searchText = '';
  private searchChangedSubject = new BehaviorSubject<string>('');
  private offersLoadedSubject = new BehaviorSubject<void>(undefined);

  private filtersChangedSubject = new BehaviorSubject<void>(undefined);

  private hasRunInitialAttention = false;
  private initialOffersReady = false;
  private logoRenderedOnInitialLoad = false;
  private hasViewModeOverride = false;
  private hasThemeModeOverride = false;
  private searchScrollTimer: ReturnType<typeof setTimeout> | null = null;
  private searchFocusTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private offerService: OfferService,
    private location: Location,
    private route: ActivatedRoute,
    public sharedService: SharedService,
    public shoppingCartService: ShoppingCartService,
    private busyService: BusyService,
    private ngZone: NgZone,
    private attentionFlash: AttentionFlashService,
    private themeService: ThemeService,
    private offerCategoryService: OfferCategoryService,
    private titleService: Title,
  ) {
    this.busyService.busy();
  }

  ngOnInit(): void {
    // Initialize logo from localStorage
    this.initializeLogo();
    this.initializeViewMode();
    this.initializeThemeMode();

    // Prevent UI rendering until a real theme has been applied.
    this.waitForThemeReady();

    // listen for external tab navigation requests from child components
    window.addEventListener('ui:new:navigateTab', (e: Event) => {
      try {
        if (!this.sharedService.isAutoSequenceEnabled()) {
          return;
        }

        const detail: any = (e as CustomEvent).detail;
        if (detail?.tab) {
          this.ngZone.run(() => this.onTabChanged(detail.tab as UiNewOfferTab));
        }
      } catch (err) {
        // ignore
      }
    });

    // listen for favorite toggles from offer cards
    window.addEventListener('ui:new:favoritesChanged', () => {
      this.ngZone.run(() => {
        this.syncFavoriteFilterAvailability();
        this.filtersChangedSubject.next();
      });
    });

    // listen for logo updates from theme-controls
    window.addEventListener('logo:updated', (e: Event) => {
      try {
        const detail: any = (e as CustomEvent).detail;
        this.logoUrl = detail?.logoUrl || null;
      } catch (err) {
        // ignore
      }
    });
    this.filteredOffers$ = merge(
      this.offersLoadedSubject.asObservable(),
      this.filtersChangedSubject.asObservable(),
      this.searchChangedSubject.asObservable(),
    ).pipe(
      map(() => this.computeFilteredOffers()),
      shareReplay(1),
    );

    // Prefer route params (works for /offer/:alias_id/:prop_id), but keep URL parsing fallback
    // so legacy /ui-new/:alias_id/:prop_id links and demo usage continue to work.
    this.offerAliasId = this.route.snapshot.paramMap.get('alias_id') ?? '';
    this.propertyId = this.route.snapshot.paramMap.get('prop_id') ?? '';

    if (!this.offerAliasId || !this.propertyId) {
      const parsed = this.parseAliasAndPropFromUrl();
      this.offerAliasId = this.offerAliasId || parsed.offerAliasId;
      this.propertyId = this.propertyId || parsed.propertyId;
    }

    // Demo page can be visited without params
    if (!this.offerAliasId || !this.propertyId) {
      this.showLandingMessage = true;
      this.loadingPage = false;
      this.initialPageLoad = false;
      this.busyService.idle();
      return;
    }

    this.shoppingCartService.offerAliasId = this.offerAliasId;
    this.shoppingCartService.propId = this.propertyId;
    this.shoppingCartService.setAllSelectedOffersFromSessionStorage();

    this.offerService
      .check_upgrade_availability(this.offerAliasId, this.propertyId, true, true, true, true)
      .subscribe({
        next: (upgradeAvailability: UpgradeAvailabilityDTO) => {
          this.upgradeAvailability = upgradeAvailability;
          this.sharedService.setUpgradeAvailability(upgradeAvailability);

          this.requestOnlyMode =
            this.sharedService.countUpgradeTypesAvailable(upgradeAvailability) === 1;
          this.sharedService.requestOnlyMode = this.requestOnlyMode;

          if (this.sharedService.countUpgradeTypesAvailable(upgradeAvailability) === 0) {
            window.location.assign('/offerNotAvailable');
            return;
          }

          this.setDefaultTab(upgradeAvailability);

          // Apply guest-configured theme (validated against theme registry) when available.
          // This is a no-op if applicationConfig hasn't been populated yet.
          try {
            const desiredTheme = this.sharedService.applicationConfig?.prestay?.theme;
            this.themeService.setThemeOrFallback(desiredTheme, 'zen');
          } catch {
            // ignore
          }

          // Release spinner once first offers emission arrives
          this.offers$.pipe(take(1)).subscribe(() => {
            this.busyService.idle();
            this.loadingPage = false;
            this.initialOffersReady = true;
            this.finishInitialLoadIfReady();

            // Run the attention flash sequence once the page is actually visible.
            if (!this.hasRunInitialAttention) {
              this.hasRunInitialAttention = true;
              const visibleTabs: AttentionFlashTab[] = this.offerCategoryService.getVisibleTabsForAttention(
                this.upgradeAvailability,
              );
              this.attentionFlash.restartSequenceFromNavBar(visibleTabs);
            }
          });
        },
        error: () => {
          this.busyService.idle();
          this.loadingPage = false;
          this.initialPageLoad = false;
        },
      });
  }

  ngOnDestroy(): void {
    if (this.searchScrollTimer) {
      clearTimeout(this.searchScrollTimer);
      this.searchScrollTimer = null;
    }

    if (this.searchFocusTimer) {
      clearTimeout(this.searchFocusTimer);
      this.searchFocusTimer = null;
    }
  }

  private parseAliasAndPropFromUrl(): { offerAliasId: string; propertyId: string } {
    const path = this.location.path(false);
    const cleanPath = path.split('?')[0].split('#')[0];
    const segments = cleanPath.split('/').filter(Boolean);

    // Support both /ui-new/:alias_id/:prop_id and /offer/:alias_id/:prop_id
    const idx = segments.indexOf('ui-new') >= 0 ? segments.indexOf('ui-new') : segments.indexOf('offer');
    if (idx >= 0 && segments.length >= idx + 3) {
      return {
        offerAliasId: segments[idx + 1] ?? '',
        propertyId: segments[idx + 2] ?? '',
      };
    }

    return { offerAliasId: '', propertyId: '' };
  }

  private initializeLogo(): void {
    const logoEnabled = localStorage.getItem('logoEnabled');
    const logoUrl = localStorage.getItem('logoUrl');

    if (logoEnabled === 'on' && logoUrl) {
      this.logoUrl = logoUrl;
    } else {
      this.logoUrl = null;
    }
  }

  private initializeViewMode(): void {
    const storedViewMode = this.getStoredViewMode();
    if (storedViewMode) {
      this.viewMode = storedViewMode;
      this.hasViewModeOverride = true;
      return;
    }

    this.viewMode = 'tiles';
    this.hasViewModeOverride = false;
  }

  private getStoredViewMode(): UiNewViewMode | null {
    try {
      const storedValue = localStorage.getItem(UI_NEW_VIEW_MODE_STORAGE_KEY);
      return storedValue === 'tiles' || storedValue === 'list' ? storedValue : null;
    } catch {
      return null;
    }
  }

  updateViewModeFromGuest(defaultTilesView: boolean): void {
    if (this.hasViewModeOverride) {
      return;
    }

    this.viewMode = defaultTilesView ? 'tiles' : 'list';
  }

  updateThemeModeFromGuest(defaultLightMode: boolean): void {
    // Precedence rule: explicit user preference always wins over guest default.
    if (this.hasThemeModeOverride) {
      return;
    }

    // Guest default uses a "light mode" boolean, while ThemeService expects dark mode.
    this.themeService.setDarkMode(!defaultLightMode);
  }

  private initializeThemeMode(): void {
    // Only treat stored mode as an override when we have an explicit user marker.
    // This prevents bootstrap writes (ThemeService init) from blocking guest defaults.
    if (!this.themeService.hasThemeModeUserOverride()) {
      this.hasThemeModeOverride = false;
      return;
    }

    const storedThemeMode = this.getStoredThemeMode();
    if (!storedThemeMode) {
      this.hasThemeModeOverride = false;
      return;
    }

    this.hasThemeModeOverride = true;
    // Restore the user's explicit mode choice for this browser.
    this.themeService.setDarkMode(storedThemeMode === 'dark');
  }

  private getStoredThemeMode(): 'light' | 'dark' | null {
    try {
      const storedValue = localStorage.getItem('theme');
      return storedValue === 'light' || storedValue === 'dark' ? storedValue : null;
    } catch {
      return null;
    }
  }

  private waitForThemeReady(): void {
    this.themeReady = this.themeService.isThemeReady();

    if (this.themeReady) return;

    this.themeService.themeReady$.subscribe(isReady => {
      this.themeReady = isReady;
    });
  }

  onTopBarLogoRendered(): void {
    this.logoRenderedOnInitialLoad = true;
    this.finishInitialLoadIfReady();
  }

  private finishInitialLoadIfReady(): void {
    if (!this.initialPageLoad) {
      return;
    }

    if (!this.initialOffersReady || !this.logoRenderedOnInitialLoad) {
      return;
    }

    this.initialPageLoad = false;
  }

  onRefresh() {
    this.setDefaultTab(this.upgradeAvailability);
  }

  toggleFilters() {
    this.filtersOpen = !this.filtersOpen;
    this.toggleSearch(this.filtersOpen);
  }

  declineOffer() {
    if (this.sharedService.offers?.length) {
      this.sharedService.offer = this.sharedService.offers[0];
      this.offerService
        .setOfferDeclined(this.sharedService.offer.prop_id, this.sharedService.offer.offer_alias_id)
        .pipe(
          map(() => {
            this.navigateToOfferDeclined();
          }),
        )
        .subscribe();
    }
  }

  navigateToOfferNotavailable() {
    window.location.assign('/offerNotAvailable');
  }

  navigateToOfferDeclined() {
    window.location.assign('/offerDeclined');
  }

  navigatePrivacy() {
    window.open(`${window.location.origin}/privacyPolicy`, '_blank');
  }

  navigateTerms() {
    window.open(`${window.location.origin}/termsOfService`, '_blank');
  }

  viewMoreOrLess(offers: Offer[] | null): string {
    const numOffers = offers ? offers.length : 0;
    let numDefaultOffers = this.sharedService.filterDefaultAndValidOffers(offers ?? []).length;

    if (this.viewMoreToggle && numOffers > numDefaultOffers) {
      this.viewMoreOrLessStr = 'View Less';
    } else if (this.viewMoreToggle && numOffers < numDefaultOffers) {
      this.viewMoreOrLessStr = 'Remove Filter';
    } else if (!this.viewMoreToggle && numOffers === numDefaultOffers) {
      this.viewMoreOrLessStr = 'View More';
    }

    return this.viewMoreOrLessStr;
  }

  toggleSearch(closeFilterOpen = true) {
    // When using the bottom bar View More/View Less control, close the filter panel
    // (equivalent to toggling filters off via the filter icon).
    this.viewMoreToggle = !this.viewMoreToggle;
    if (!this.viewMoreToggle) {
      this.filtersOpen = false;
    } else {
      this.filtersOpen = true; 
    }
    
    if (this.viewMoreToggle) {
      // reset search when entering View More mode
      this.onSearchChanged('');
    }

    // Force both filters to 'All' when toggling view state
    this.tagsDict['All'] = { flg_enabled: true };
    this.tags.forEach(tag => {
      if (tag !== 'All') {
        this.tagsDict[tag] = { flg_enabled: false };
      }
    });

    this.bedTypesDict['All'] = { flg_enabled: true };
    this.bedTypes.forEach(type => {
      if (type !== 'All') {
        this.bedTypesDict[type] = { flg_enabled: false };
      }
    });

    // Also reset the featured filter when toggling view state
    this.featuredOnly = false;
    // And reset favorites filter when toggling view state
    this.favoriteOnly = false;

    this.filtersChangedSubject.next();
  }

  onTabChanged(tab: UiNewOfferTab) {
    // Avoid stale offer content during post-initial tab switches, but keep the shell and inline
    // spinner host mounted so non-fullscreen loading can render.
    this.loadingPage = true;

    this.selectedTab = tab;
    this.sharedService.setOfferFilter(tab);

    if (tab === 'ROOM') {
      this.getAndSetRoomOfferSet();
    } else {
      this.getAndSetOfferSet(tab);
    }
  }

  setDefaultTab(upgradeAvailability: UpgradeAvailabilityDTO) {
    const firstAvailableKey = this.getFirstAvailableKey(upgradeAvailability);
    const tab = (firstAvailableKey ?? 'ROOM') as UiNewOfferTab;
    this.onTabChanged(tab);
  }

  getFirstAvailableKey(upgrade: UpgradeAvailabilityDTO): UiNewOfferTab | null {
    return this.offerCategoryService.getFirstAvailableCategory(upgrade);
  }

  getAndSetRoomOfferSet() {
    this.loadingPage = true;
    this.busyService.busy('inline');

    const offerSetId$ = this.getExistingOfferSetId(this.propertyId, this.offerAliasId, 'ROOM');

    const offers$ = offerSetId$.pipe(
      mergeMap(offerSetId =>
        this.offerService
          .getFullOfferData(this.offerAliasId, this.propertyId, this, 'ROOM', false, offerSetId)
          .pipe(
            shareReplay(1),
            map(offers => (Array.isArray(offers) ? offers : [])),
            tap(() => {
              this.offerService
                .getOfferExpires(this.offerAliasId, this.propertyId)
                .subscribe((expires: OfferExpiresDTO) => {
                  if (expires?.ts_expires) this.endDate = new Date(expires.ts_expires);
                });
            }),
            catchError(() => { this.navigateToOfferNotavailable(); return of([] as Offer[]); }),
          )
      ),
      shareReplay(1),
    );

    this.offers$ = offers$;

    this.offers$.pipe(take(1)).subscribe(() => {
      this.syncFeaturedFilterAvailability();
      this.syncFavoriteFilterAvailability();
      this.offersLoadedSubject.next();
      this.busyService.idle('inline');
      this.loadingPage = false;
      this.updateDocumentTitle();
    });
  }

  private getExistingOfferSetId(property_id: string, offer_alias_id: string, checkOutType: UiNewOfferTab): Observable<string> {
    
    return this.offerService
      .getExistingOfferset(offer_alias_id, property_id, checkOutType)
      .pipe(
        shareReplay(1),
        map((existingOfferSet: ExistingOffersetDTO | null) => {
          if (existingOfferSet && existingOfferSet.offer_set_id) {
            return existingOfferSet.offer_set_id;
          }
          return UiNewHomeComponent.EMPTY_OFFER_SET_ID;
        }),
        catchError(() => of(UiNewHomeComponent.EMPTY_OFFER_SET_ID)),
      );
  }

  getAndSetOfferSet(offerCategory: UiNewOfferTab) {
    this.loadingPage = true;
    this.busyService.busy('inline');

    const offerSetId$ = this.getExistingOfferSetId(this.propertyId, this.offerAliasId, offerCategory);

    const offers$ = offerSetId$.pipe(
      mergeMap(offerSetId =>
        this.offerService
          .getFullOfferData(this.offerAliasId, this.propertyId, this, offerCategory, true, offerSetId)
          .pipe(
            shareReplay(1),
            map(offers => Array.isArray(offers) ? offers : []),
            tap(() => {
              this.offerService
                .getOfferExpires(this.offerAliasId, this.propertyId)
                .subscribe((expires: OfferExpiresDTO) => {
                  if (expires?.ts_expires) this.endDate = new Date(expires.ts_expires);
                });
            }),
            catchError(() => { this.navigateToOfferNotavailable(); return of([] as Offer[]); }),
          )
      ),
      shareReplay(1),
    );

    this.offers$ = offers$;

    this.offers$.pipe(take(1)).subscribe((offers) => {
      this.syncFeaturedFilterAvailability();
      this.syncFavoriteFilterAvailability();
      this.offersLoadedSubject.next();
      this.busyService.idle('inline');
      this.loadingPage = false;
      this.updateDocumentTitle();
    });
  }

  onSearchChanged(value: string) {
    this.searchText = value;
    this.searchChangedSubject.next(value);
    this.scheduleScrollToTopAfterSearch();
  }

  private updateDocumentTitle(): void {
    const name = this.sharedService.propertyName?.trim();
    this.titleService.setTitle(name ? `${name} | Offers` : 'Hotel Offers');
  }

  private scheduleScrollToTopAfterSearch(): void {
    if (this.searchScrollTimer) {
      clearTimeout(this.searchScrollTimer);
    }

    if (this.searchFocusTimer) {
      clearTimeout(this.searchFocusTimer);
      this.searchFocusTimer = null;
    }

    // Keep this within one second while giving the UI a moment to update.
    this.searchScrollTimer = setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      this.searchFocusTimer = setTimeout(() => {
        this.filterPanelComponent?.focusSearchInput();
        this.searchFocusTimer = null;
      }, 300);
      this.searchScrollTimer = null;
    }, 250);
  }

  onTagClicked(tag: string) {
    this.filterTags('tag', tag);
  }

  onBedTypeClicked(bedType: string) {
    this.filterTags('bedType', bedType);
  }

  onFeaturedClicked() {
    this.featuredOnly = !this.featuredOnly;
    this.filtersChangedSubject.next();
  }

  onFavoriteClicked() {
    this.favoriteOnly = !this.favoriteOnly;
    this.filtersChangedSubject.next();
  }

  getFeaturedOfferCount(): number {
    const featureOfferCount = this.offers
      .filter(offer => offer.flg_default_offer === true)
      .length

    return featureOfferCount
  }

  private computeFilteredOffers(): Offer[] {
    // Base candidate set:
    // - Default view (room + not viewMore): use default/valid offers
    // - Otherwise: use search-filtered offers

    const baseCandidates =
      this.selectedTab === 'ROOM' && !this.viewMoreToggle
        ? this.sharedService.filterDefaultAndValidOffers(this.offers)
        : this.filterOffersBySearchString(this.searchText);

    const tagOfferIds = this.getOfferIdsFilteredByTagsOrBedTypes('tag');
    const bedTypeOfferIds = this.getOfferIdsFilteredByTagsOrBedTypes('bedType');
    const featuredOfferIds = this.getOfferIdsFilteredByFeatured();
    const favoriteOfferIds = this.getOfferIdsFilteredByFavorites();

    const filtered = baseCandidates
      .filter(
        offer =>
          bedTypeOfferIds.has(offer.offer_id) &&
          tagOfferIds.has(offer.offer_id) &&
          featuredOfferIds.has(offer.offer_id) &&
          favoriteOfferIds.has(offer.offer_id),
      )
      .sort((a, b) => a.display_order - b.display_order);

    return filtered;
  }

  private getOfferIdsFilteredByFavorites(): Set<string> {
    const allIds = new Set((this.offers ?? []).map(o => o.offer_id));
    if (!this.favoriteOnly) {
      return allIds;
    }

    const favoriteIds = new Set(
      (this.offers ?? []).filter(o => o?.flg_favorite === true).map(o => o.offer_id),
    );
    return favoriteIds;
  }

  private getOfferIdsFilteredByFeatured(): Set<string> {
    const allIds = new Set((this.offers ?? []).map(o => o.offer_id));
    if (!this.featuredOnly) {
      return allIds;
    }

    const featuredIds = new Set(
      (this.offers ?? []).filter(o => o?.flg_default_offer === true).map(o => o.offer_id),
    );
    return featuredIds;
  }

  private syncFeaturedFilterAvailability(): void {
    this.hasFeaturedOffers = (this.offers ?? []).some(o => o?.flg_default_offer === true);
    if (!this.hasFeaturedOffers) {
      this.featuredOnly = false;
    }
  }

  private syncFavoriteFilterAvailability(): void {
    this.hasFavoriteOffers = (this.offers ?? []).some(o => o?.flg_favorite === true);
    if (!this.hasFavoriteOffers) {
      this.favoriteOnly = false;
    }
  }

  private searchToArray(searchStr: string): string[] {
    return searchStr
      .toLocaleLowerCase()
      .split(/[ ,]+/)
      .map(s => s.trim())
      .filter(s => s !== '');
  }

  private isNumber(value?: string | number): boolean {
    return value != null && value !== '' && !isNaN(Number(value.toString()));
  }

  private filterOffersBySearchString(searchStr: string): Offer[] {
    if (!this.offers) {
      return [];
    }

    const trimmed = (searchStr ?? '').trim();
    if (!trimmed) {
      return this.offers.slice().sort((a, b) => a.display_order - b.display_order);
    }

    const searchTerms = this.searchToArray(trimmed.toLowerCase());

    return this.offers
      .filter(offer => {
        const amenities = offer.amenities;
        const searchSpace = (
          (Array.isArray(amenities?.upsell_attributes) ? amenities.upsell_attributes.join(' ') : '') +
          ' ' +
          (amenities?.upsell_room_search_description ?? '') +
          ' ' +
          (amenities?.summary ?? '')
        ).toLowerCase();

        return searchTerms.every(term => {
          if (term.includes('$')) {
            const num = Number(term.replace('$', ''));
            return this.isNumber(num) && Math.round(offer.ofr_price) <= num;
          } else if (this.isNumber(term)) {
            return (amenities?.sqft ?? 0) >= Number(term);
          }

          return searchSpace.includes(term);
        });
      })
      .sort((a, b) => a.display_order - b.display_order);
  }

  private getOfferIdsFilteredByTagsOrBedTypes(type: 'tag' | 'bedType'): Set<string> {
    const dict = type === 'tag' ? this.tagsDict : this.bedTypesDict;
    const map = type === 'tag' ? this.tagsOfOfferIds : this.bedTypesOfOfferIds;

    // If "All" is selected, allow all offers for this dimension
    if (dict['All']?.flg_enabled) {
      const allIds = map['All'] ?? [];
      return new Set(allIds);
    }

    // Gather all specifically enabled filters (excluding "All")
    const activeKeys = Object.keys(dict).filter(key => key !== 'All' && dict[key]?.flg_enabled);

    if (activeKeys.length === 0) {
      // Nothing selected -> no restriction from this dimension
      return new Set((this.offers ?? []).map(o => o.offer_id));
    }

    // Start with the first set, then intersect
    let result = new Set<string>(map[activeKeys[0]] ?? []);

    for (let i = 1; i < activeKeys.length; i++) {
      const idsForKey = new Set<string>(map[activeKeys[i]] ?? []);
      result = new Set([...result].filter(id => idsForKey.has(id)));
    }

    return result;
  }

  private filterTags(filterName: 'tag' | 'bedType', filterValue: string): void {
    if (filterName === 'tag') {
      const current = this.tagsDict[filterValue]?.flg_enabled ?? false;
      this.tagsDict[filterValue] = { flg_enabled: !current };

      if (filterValue !== 'All') {
        this.tagsDict['All'] = { flg_enabled: false };
      } else {
        this.tags.forEach(tag => {
          if (tag !== 'All') {
            this.tagsDict[tag] = { flg_enabled: false };
          }
        });
      }

      this.filtersChangedSubject.next();
      return;
    }

    const bedTypeCount = this.bedTypes.length;
    let bedTypeFilterCount = 0;
    this.bedTypes.forEach(type => {
      if (this.bedTypesDict[type]?.flg_enabled) {
        bedTypeFilterCount++;
      }
    });

    if (bedTypeCount - 1 === bedTypeFilterCount) {
      filterValue = 'All';
    }

    const current = this.bedTypesDict[filterValue]?.flg_enabled ?? false;
    this.bedTypesDict[filterValue] = { flg_enabled: !current };

    if (filterValue !== 'All') {
      this.bedTypesDict['All'] = { flg_enabled: false };
    } else {
      this.bedTypes.forEach(type => {
        if (type !== 'All') {
          this.bedTypesDict[type] = { flg_enabled: false };
        }
      });
    }

    this.filtersChangedSubject.next();
  }

}
